import { useState, useCallback, useRef, useEffect } from "react";
import { useAtelierStore, JOB_STATUSES, getJobStatusFromOrdre, isMirroredJob, isMirroredCharge } from "@store/atelierStore";
import { getOrdreById } from "@store/ordreStore";
import { WorkCharge } from "../../types/garage/atelier";
import { snapToGrid } from "@utils/timeGrid";
import { planningArrowsAllowed } from "@utils/planningArrowKeys";
import { FloatingGhost } from "./FloatingGhostOverlay";
import { getMonday, toISO, AvailabilityBlock } from "@store/disponibiliteStore";
import { showNotification } from "context/notification.service";
import {
  isMetierMismatchSnoozed,
  snoozeMetierMismatch,
  SNOOZE_OPTIONS,
  type SnoozeDuration,
} from "./metierMismatchSnooze";
import { useDataStore } from "@store/user_org";
import { useDispoAtelierStore } from "@store/dispoAtelierStore";
import { blockSegmentOnDate, firstOverlappingOffRegion, workerOffIntervals } from "@utils/blockSegments";
import {
  GridPreview,
  HOURS_START,
  HOURS_END,
  SNAP_HOUR,
  ATELIER_SNAP_HOUR,
  atelierMetrics,
} from "./gridTypes";
import { findSameOrdreTouching, mergedRange } from "./mergeRange";
import { splitAroundBusy } from "./splitAroundBusy";

/** Check if placing a job would overlap with existing jobs for the same worker */
function hasOverlap(
  workerId: string,
  date: string,
  startHour: number,
  durationHours: number,
  excludeJobId?: string,
): boolean {
  const jobs = useAtelierStore.getState().jobs;
  const end = startHour + durationHours;
  return jobs.some((j) => {
    if (j.id === excludeJobId) return false;
    if (j.workerId !== workerId || j.date !== date) return false;
    const jEnd = j.startHour + j.durationHours;
    // Tolérance : 8 + 1,35 ne vaut pas exactement 9,35 en virgule flottante.
    return startHour < jEnd - EPS_HOUR && end > j.startHour + EPS_HOUR;
  });
}

const EPS_HOUR = 1e-6;

/**
 * Colle le créneau au travail voisin quand il ne le mord que de moins d'un pas de
 * grille. Une durée hors quart d'heure (1,35 h) fait finir le travail précédent entre
 * deux crans : le cran d'avant chevauche, celui d'après laisse un trou — Freddy devait
 * laisser un trou pour mettre deux RDV à la suite (Héric 2026-10-13). Rend le début
 * ajusté, ou le début demandé si le coller ne libère pas la place.
 */
export function snapAgainstNeighbours(
  workerId: string,
  date: string,
  startHour: number,
  durationHours: number,
  excludeJobId?: string,
): number {
  const jobs = useAtelierStore.getState().jobs.filter(
    (j) => j.id !== excludeJobId && j.workerId === workerId && j.date === date,
  );
  const end = startHour + durationHours;
  let start = startHour;
  for (const j of jobs) {
    const jEnd = j.startHour + j.durationHours;
    if (j.startHour < startHour && jEnd > startHour + EPS_HOUR && jEnd - startHour < ATELIER_SNAP_HOUR - EPS_HOUR) {
      start = Math.max(start, jEnd); // mord la fin du précédent : on démarre à sa fin
    }
  }
  if (start === startHour) {
    for (const j of jobs) {
      if (j.startHour > startHour && end > j.startHour + EPS_HOUR && end - j.startHour < ATELIER_SNAP_HOUR - EPS_HOUR) {
        start = Math.min(start, j.startHour - durationHours); // mord le début du suivant
      }
    }
  }
  if (start === startHour || start < HOURS_START) return startHour;
  return hasOverlap(workerId, date, start, durationHours, excludeJobId) ? startHour : start;
}

/** Travail du même ordre que le créneau chevauche ou touche (cible d'une fusion). */
function findMergeableSameOrdreJob(
  workerId: string,
  date: string,
  startHour: number,
  durationHours: number,
  ordreId: string,
  excludeJobId?: string,
) {
  return findSameOrdreTouching(useAtelierStore.getState().jobs, workerId, date, startHour, durationHours, ordreId, excludeJobId);
}

/** Check if overlap exists ONLY with non-same-ordre jobs (true overlap, not a merge candidate) */
function hasNonMergeableOverlap(
  workerId: string,
  date: string,
  startHour: number,
  durationHours: number,
  excludeJobId: string,
  ordreId?: string,
): boolean {
  const jobs = useAtelierStore.getState().jobs;
  const end = startHour + durationHours;
  return jobs.some((j) => {
    if (j.id === excludeJobId) return false;
    if (j.workerId !== workerId || j.date !== date) return false;
    const jEnd = j.startHour + j.durationHours;
    if (!(startHour < jEnd && end > j.startHour)) return false;
    // Same-ordre overlap is a merge candidate, not a real conflict
    if (ordreId && j.ordre_id === ordreId) return false;
    return true;
  });
}

/**
 * Travail du même ordre dans lequel une charge lâchée ici se fondrait. Jamais vers ou
 * depuis un magnet miroir MyO (garage non « Carvis fait foi ») : la fusion passerait
 * à côté de MyO — on garde alors le refus d'un créneau occupé.
 */
const fmtHour = (h: number) => {
  const m = Math.round((h % 1) * 60);
  return `${Math.floor(h)}h${m ? String(m).padStart(2, "0") : ""}`;
};

/** Charge lâchée par-dessus un travail : posée dans les trous jusqu'à la fin de journée
 *  (splitAroundBusy), le reste demeure dans la charge. Une charge MyO est posée morceau
 *  après morceau — chaque placement attend le précédent, comme la scission de pause. */
function placeChargeAroundBusy(chargeId: string, workerId: string, date: string, fromHour: number) {
  const st = useAtelierStore.getState();
  const charge = st.charges.find((c) => c.id === chargeId);
  if (!charge) return;
  const busy: [number, number][] = [
    ...st.jobs
      .filter((j) => j.workerId === workerId && j.date === date)
      .map((j) => [j.startHour, j.startHour + j.durationHours] as [number, number]),
    ...workerOffIntervals(useDispoAtelierStore.getState().blocks, workerId, date, HOURS_START, HOURS_END),
  ];
  const segments = splitAroundBusy(fromHour, HOURS_END, charge.hours, busy);
  if (!segments.length) {
    showNotification({ type: "error", message: "Impossible : plus de place libre ce jour-là après ce créneau" });
    return;
  }
  if (isMirroredCharge(charge)) {
    segments.reduce<Promise<boolean>>((prev, s) => prev.then((ok) => ok && useAtelierStore.getState().runMyo("place", {
      charge_id: chargeId, worker_id: workerId, date, start_hour: s.startHour, duration_hours: s.duration,
    }, "Planification dans MyO…")), Promise.resolve(true));
  } else {
    for (const s of segments) {
      useAtelierStore.getState().placeChargeAsJob(chargeId, workerId, s.startHour, s.duration, date);
    }
  }
  const placed = segments.reduce((n, s) => n + s.duration, 0);
  const left = +(charge.hours - placed).toFixed(2);
  showNotification({
    type: "success",
    message: `Scindé autour des travaux déjà prévus : ${segments.map((s) => `${fmtHour(s.startHour)}–${fmtHour(s.startHour + s.duration)}`).join(", ")}`
      + (left > 0 ? ` — ${left} h restent à planifier` : ""),
  });
}

function findChargeMergeTarget(
  chargeId: string,
  ordreId: string | undefined,
  workerId: string,
  date: string,
  startHour: number,
  durationHours: number,
) {
  if (!ordreId) return null;
  const charge = useAtelierStore.getState().charges.find((c) => c.id === chargeId);
  if (!charge || isMirroredCharge(charge)) return null;
  const target = findMergeableSameOrdreJob(workerId, date, startHour, durationHours, ordreId);
  return target && !isMirroredJob(target) ? target : null;
}

/**
 * Heures posables pour une charge déposée à `startHour` : une charge plus longue que
 * ce qui reste de la journée du mécanicien s'arrête à sa fin de journée, le reste
 * retourne dans « à planifier » (placeChargeAsJob garde le solde sur la charge).
 * La pause déjeuner n'est pas une fin de journée : elle reste traitée par la modale.
 */
function placeableChargeHours(workerId: string, date: string, startHour: number, hours: number): number {
  const off = workerOffIntervals(useDispoAtelierStore.getState().blocks, workerId, date, HOURS_START, HOURS_END);
  const dayEnd = workerDayEnd(workerId, date, startHour);
  // La pause déjeuner ne se travaille pas : 10 h lâchées à 8 h sur une journée
  // 8h-12h / 13h30-18h n'en posent que 8,5 (Steeven, 2026-10-06). Sans ce décompte,
  // la modale de pause ne pouvait découper qu'en débordant avant 8 h ou après 18 h.
  const pauses = off.reduce((sum, [s, e]) => sum + Math.max(0, Math.min(e, dayEnd) - Math.max(s, startHour)), 0);
  const room = snapToGrid(dayEnd - startHour - pauses, ATELIER_SNAP_HOUR);
  return room > 0 ? Math.min(hours, room) : Math.min(hours, HOURS_END - startHour);
}

/** Fin de journée du compagnon après `fromHour` : début de sa dernière plage non
 *  travaillée quand elle court jusqu'au bout de la grille, sinon la fin de grille. */
function workerDayEnd(workerId: string, date: string, fromHour: number): number {
  const off = workerOffIntervals(useDispoAtelierStore.getState().blocks, workerId, date, HOURS_START, HOURS_END);
  const tail = off[off.length - 1];
  return tail && tail[1] >= HOURS_END && tail[0] > fromHour ? tail[0] : HOURS_END;
}

function getStatusConfig(status: string) {
  return JOB_STATUSES.find((s) => s.id === status)!;
}

/**
 * Find the first non-working ("off") region that the dropped range overlaps for a
 * worker: the lunch break, an explicit pause/congé, or any slot the worker has no
 * work zone for. Returns its bounds so the caller can offer split/force, or null
 * when the drop sits entirely within productive zones.
 *
 * Note: any overlap counts, including a drop *fully inside* the break — that's the
 * "dropped on the lunch break" case, which must still flag (the split modal then
 * only offers "Forcer" since there's nothing to split around).
 */
function findOverlappingPauseZone(
  workerId: string,
  date: string,
  startHour: number,
  endHour: number,
): { start: number; end: number } | null {
  const blocks = useDispoAtelierStore.getState().blocks;
  return firstOverlappingOffRegion(blocks, workerId, date, startHour, endHour);
}

/**
 * Determine whether dropping a magnet of `magnetMetier` at this slot lands on a
 * dispo zone of a DIFFERENT métier (e.g. a mécanique chantier on a diag dispo).
 * Returns the conflicting zone métier, or null when there's no conflict:
 *  - if any overlapping productive zone matches `magnetMetier` → null (worker is available for it)
 *  - if no productive zone overlaps the slot → null (silent, today's behavior)
 *  - "autre" magnets never conflict (catch-all)
 * pause/conge zones are ignored here (handled by the lunch/pause check).
 */
function getMismatchZone(
  workerId: string,
  date: string,
  startHour: number,
  endHour: number,
  magnetMetier: string,
): string | null {
  if (!magnetMetier || magnetMetier === "autre") return null;
  const blocks = useDispoAtelierStore.getState().blocks;
  let mismatchZone: string | null = null;
  for (const b of blocks) {
    if (b.workerId !== workerId) continue;
    const zone = b.zoneType || "mecanique";
    if (zone === "pause" || zone === "conge") continue;
    const seg = blockSegmentOnDate(b, date);
    if (!seg) continue;
    if (startHour < seg.endHour && endHour > seg.startHour) {
      if (zone === magnetMetier) return null; // available for this métier → no prompt
      if (!mismatchZone) mismatchZone = zone;
    }
  }
  return mismatchZone;
}

export interface ReportPausePending {
  chargeId: string;
  targetStatus: "en_pause";
  clientName: string;
}

export interface LunchOverlapPending {
  type: "job" | "charge";
  jobId?: string;
  chargeId?: string;
  workerId: string;
  startHour: number;
  durationHours: number;
  date: string;
  lunchStart: number;
  lunchEnd: number;
  /** Horaires du compagnon ce jour-là : la modale n'offre pas de découpe qui en sort. */
  dayStart?: number;
  dayEnd?: number;
}

export interface JobMergePending {
  draggedJobId: string;
  /** Fusion d'une charge (et non d'un travail) dans le travail cible. */
  draggedChargeId?: string;
  targetJobId: string;
  mergedStartHour: number;
  mergedDurationHours: number;
  draggedClientName: string;
  targetClientName: string;
}

export interface MetierMismatchPending {
  type: "job" | "charge";
  jobId?: string;
  chargeId?: string;
  workerId: string;
  startHour: number;
  durationHours: number;
  date: string;
  /** The compagnon's dispo zone métier at the drop slot */
  zoneMetier: string;
  /** The dragged magnet's métier */
  magnetMetier: string;
}

export function useAtelierDrag(
  gridScrollRef: React.RefObject<HTMLDivElement | null>,
  sidebarRef: React.RefObject<HTMLDivElement | null>,
) {
  const updateJob = useAtelierStore((s) => s.updateJob);
  const placeChargeAsJob = useAtelierStore((s) => s.placeChargeAsJob);
  const requestJobRemoval = useAtelierStore((s) => s.requestJobRemoval);
  const moveChargeToStatus = useAtelierStore((s) => s.moveChargeToStatus);
  const mergeCharges = useAtelierStore((s) => s.mergeCharges);
  const mergeJobs = useAtelierStore((s) => s.mergeJobs);
  const mergeChargeIntoJob = useAtelierStore((s) => s.mergeChargeIntoJob);
  const getFilteredJobs = useAtelierStore((s) => s.getFilteredJobs);

  const [lunchOverlapPending, setLunchOverlapPending] = useState<LunchOverlapPending | null>(null);
  const [reportPausePending, setReportPausePending] = useState<ReportPausePending | null>(null);
  const [jobMergePending, setJobMergePending] = useState<JobMergePending | null>(null);
  const [metierMismatchPending, setMetierMismatchPending] = useState<MetierMismatchPending | null>(null);

  // Charge drag from sidebar
  const chargeDragRef = useRef<{
    chargeId: string;
    hours: number;
    clientName: string;
    ordreId?: string;
  } | null>(null);

  // Job drag from grid
  const jobDragRef = useRef<{
    jobId: string;
    mode: "move" | "resize";
    startY: number;
    origStartHour: number;
    origDuration: number;
    origWorkerId: string;
  } | null>(null);

  const [gridPreview, _setGridPreview] = useState<GridPreview | null>(null);
  const gridPreviewRef = useRef<GridPreview | null>(null);
  const setGridPreview = useCallback((v: GridPreview | null) => {
    gridPreviewRef.current = v;
    _setGridPreview(v);
  }, []);
  const [floatingGhost, setFloatingGhost] = useState<FloatingGhost | null>(
    null,
  );
  const [overDropZone, _setOverDropZone] = useState(false);
  const overDropZoneRef = useRef(false);
  const setOverDropZone = useCallback((v: boolean) => {
    overDropZoneRef.current = v;
    _setOverDropZone(v);
  }, []);
  const [hoveredChargeSection, _setHoveredChargeSection] = useState<
    WorkCharge["status"] | null
  >(null);
  const hoveredChargeSectionRef = useRef<WorkCharge["status"] | null>(null);
  const setHoveredChargeSection = useCallback((v: WorkCharge["status"] | null) => {
    hoveredChargeSectionRef.current = v;
    _setHoveredChargeSection(v);
  }, []);

  // Charge-on-charge merge target
  const [mergeTargetChargeId, _setMergeTargetChargeId] = useState<string | null>(null);
  const mergeTargetChargeIdRef = useRef<string | null>(null);
  const setMergeTargetChargeId = useCallback((v: string | null) => {
    mergeTargetChargeIdRef.current = v;
    _setMergeTargetChargeId(v);
  }, []);

  const detectWorkerCol = useCallback(
    (clientX: number): string | null => {
      if (!gridScrollRef.current) return null;
      const cols =
        gridScrollRef.current.querySelectorAll<HTMLElement>("[data-worker-col]");
      let found: string | null = null;
      cols.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (clientX >= r.left && clientX < r.right)
          found = el.getAttribute("data-worker-col");
      });
      return found;
    },
    [gridScrollRef],
  );

  const detectDayDate = useCallback(
    (clientX: number): string | null => {
      if (!gridScrollRef.current) return null;
      const cols =
        gridScrollRef.current.querySelectorAll<HTMLElement>("[data-day-date]");
      let found: string | null = null;
      cols.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (clientX >= r.left && clientX < r.right)
          found = el.getAttribute("data-day-date");
      });
      return found;
    },
    [gridScrollRef],
  );

  const isOverGrid = useCallback(
    (clientX: number, clientY: number): boolean => {
      if (!gridScrollRef.current) return false;
      const r = gridScrollRef.current.getBoundingClientRect();
      // La grille défile dans son propre cadre : seule sa partie visible compte
      // (sinon les filtres au-dessus passeraient pour la grille défilée).
      const frame = gridScrollRef.current.closest(".overflow-auto")?.getBoundingClientRect();
      const top = frame ? Math.max(r.top, frame.top) : r.top;
      const bottom = frame ? Math.min(r.bottom, frame.bottom) : r.bottom;
      return (
        clientX >= r.left &&
        clientX <= r.right &&
        clientY >= top &&
        clientY <= bottom
      );
    },
    [gridScrollRef],
  );

  const isOverSidebar = useCallback(
    (clientX: number, clientY: number): boolean => {
      if (!sidebarRef.current) return false;
      const r = sidebarRef.current.getBoundingClientRect();
      return (
        clientX >= r.left &&
        clientX <= r.right &&
        clientY >= r.top &&
        clientY <= r.bottom
      );
    },
    [sidebarRef],
  );

  const detectChargeSection = useCallback(
    (clientX: number, clientY: number): WorkCharge["status"] | null => {
      if (!sidebarRef.current) return null;
      const sections = sidebarRef.current.querySelectorAll<HTMLElement>(
        "[data-charge-section]",
      );
      let found: WorkCharge["status"] | null = null;
      sections.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (
          clientX >= r.left &&
          clientX <= r.right &&
          clientY >= r.top &&
          clientY <= r.bottom
        ) {
          found = el.getAttribute(
            "data-charge-section",
          ) as WorkCharge["status"];
        }
      });
      return found;
    },
    [sidebarRef],
  );

  /** Detect which charge card the pointer is over, returning its id + ordre_id */
  const detectChargeCard = useCallback(
    (clientX: number, clientY: number): { id: string; ordreId: string } | null => {
      if (!sidebarRef.current) return null;
      const cards = sidebarRef.current.querySelectorAll<HTMLElement>("[data-charge-id]");
      let found: { id: string; ordreId: string } | null = null;
      cards.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom) {
          found = {
            id: el.getAttribute("data-charge-id")!,
            ordreId: el.getAttribute("data-charge-ordre") ?? "",
          };
        }
      });
      return found;
    },
    [sidebarRef],
  );

  const handleGlobalPointerMove = useCallback(
    (e: React.PointerEvent) => {
      // Resolve pixel metrics for the current compaction fresh each move (read
      // via getState so no stale closure over the density setting).
      const { ATELIER_SNAP_PX, HOUR_BLOCK_HEIGHT } = atelierMetrics(
        useAtelierStore.getState().compaction,
      );
      // --- Job drag ---
      const jd = jobDragRef.current;
      if (jd) {
        if (jd.mode === "resize") {
          const deltaY = e.clientY - jd.startY;
          const snapDelta = Math.round(deltaY / ATELIER_SNAP_PX);
          const newDur = snapToGrid(
            jd.origDuration + snapDelta * ATELIER_SNAP_HOUR,
            ATELIER_SNAP_HOUR,
          );
          const clamped = Math.max(
            ATELIER_SNAP_HOUR,
            Math.min(newDur, HOURS_END - jd.origStartHour),
          );
          const dayDate = detectDayDate(e.clientX);
          const resizeDate = dayDate || useAtelierStore.getState().selectedDate;
          setGridPreview({
            type: "job",
            jobId: jd.jobId,
            startHour: jd.origStartHour,
            durationHours: clamped,
            workerId: jd.origWorkerId,
            date: dayDate ?? undefined,
            overlap: hasOverlap(jd.origWorkerId, resizeDate, jd.origStartHour, clamped, jd.jobId),
          });
          setFloatingGhost(null);
          setOverDropZone(false);
          return;
        }

        const overGrid = isOverGrid(e.clientX, e.clientY);
        const overSide = isOverSidebar(e.clientX, e.clientY);

        if (overGrid) {
          const deltaY = e.clientY - jd.startY;
          const snapDelta = Math.round(deltaY / ATELIER_SNAP_PX);
          const newStart = snapToGrid(
            jd.origStartHour + snapDelta * ATELIER_SNAP_HOUR,
            ATELIER_SNAP_HOUR,
          );
          const newWorkerId = detectWorkerCol(e.clientX) ?? jd.origWorkerId;
          const dayDate = detectDayDate(e.clientX);
          const moveDate = dayDate || useAtelierStore.getState().selectedDate;
          const clamped = snapAgainstNeighbours(
            newWorkerId,
            moveDate,
            Math.max(HOURS_START, Math.min(newStart, HOURS_END - jd.origDuration)),
            jd.origDuration,
            jd.jobId,
          );
          const draggedJob = useAtelierStore.getState().jobs.find((j) => j.id === jd.jobId);
          const dragOrdreId = draggedJob?.ordre_id;
          // Only flag as overlap if conflict is with a different ordre (same-ordre = merge candidate)
          const realOverlap = dragOrdreId
            ? hasNonMergeableOverlap(newWorkerId, moveDate, clamped, jd.origDuration, jd.jobId, dragOrdreId)
            : hasOverlap(newWorkerId, moveDate, clamped, jd.origDuration, jd.jobId);
          const mergeCandidateJob = dragOrdreId
            ? findMergeableSameOrdreJob(newWorkerId, moveDate, clamped, jd.origDuration, dragOrdreId, jd.jobId)
            : null;
          if (mergeCandidateJob) console.log("[merge-preview]", mergeCandidateJob.id, mergeCandidateJob.clientName);
          setGridPreview({
            type: "job",
            jobId: jd.jobId,
            startHour: clamped,
            durationHours: jd.origDuration,
            workerId: newWorkerId,
            date: dayDate ?? undefined,
            overlap: realOverlap,
            mergeTarget: !!mergeCandidateJob,
            mergeTargetJobId: mergeCandidateJob?.id,
          });
          setFloatingGhost(null);
          setOverDropZone(false);
        } else {
          const origJob = getFilteredJobs().find((j) => j.id === jd.jobId);
          const config = origJob
            ? (() => {
                const st = origJob.ordreStatus
                  || (origJob.ordre_id ? getOrdreById(origJob.ordre_id)?.status : undefined);
                if (st) return getStatusConfig(getJobStatusFromOrdre(st as never));
                return getStatusConfig(origJob.status);
              })()
            : JOB_STATUSES[0];
          setGridPreview(null);
          setFloatingGhost({
            x: e.clientX,
            y: e.clientY,
            width: 160,
            height: 40,
            clientName: origJob?.clientName ?? "",
            hours: jd.origDuration,
            color: config.color,
            bgColor: config.bgColor,
          });
          setOverDropZone(overSide);
        }
        return;
      }

      // --- Charge drag ---
      const cd = chargeDragRef.current;
      if (cd) {
        const overGrid = isOverGrid(e.clientX, e.clientY);

        if (overGrid) {
          const workerId = detectWorkerCol(e.clientX);
          const dayDate = detectDayDate(e.clientX);
          if (workerId && gridScrollRef.current) {
            const gridRect = gridScrollRef.current.getBoundingClientRect();
            const relY =
              e.clientY - gridRect.top + gridScrollRef.current.scrollTop;
            const rawHour = HOURS_START + relY / HOUR_BLOCK_HEIGHT;
            // Une charge plus longue que la journée (ex. 18,7 h) ne doit pas être
            // collée à 7 h : elle part où on la lâche et s'arrête en fin de journée.
            const gridStart = snapToGrid(
              Math.max(HOURS_START, Math.min(rawHour, HOURS_END - (cd.hours < HOURS_END - HOURS_START ? cd.hours : ATELIER_SNAP_HOUR))),
              ATELIER_SNAP_HOUR,
            );
            const chargeDate = dayDate || useAtelierStore.getState().selectedDate;
            const startHour = snapAgainstNeighbours(
              workerId, chargeDate, gridStart, placeableChargeHours(workerId, chargeDate, gridStart, cd.hours),
            );
            const durationHours = placeableChargeHours(workerId, chargeDate, startHour, cd.hours);
            // Un travail du même ordre touché n'est pas un conflit : la charge s'y fondra.
            const mergeTarget = findChargeMergeTarget(cd.chargeId, cd.ordreId, workerId, chargeDate, startHour, durationHours);
            setGridPreview({
              type: "charge",
              chargeId: cd.chargeId,
              startHour,
              durationHours,
              workerId,
              clientName: cd.clientName,
              date: dayDate ?? undefined,
              overlap: mergeTarget
                ? hasNonMergeableOverlap(workerId, chargeDate, startHour, durationHours, "", cd.ordreId)
                : hasOverlap(workerId, chargeDate, startHour, durationHours),
              mergeTargetJobId: mergeTarget?.id,
            });
          } else {
            // Over the grid area but not on a worker column (e.g. hour labels)
            // Keep last preview if any, clear ghost so it doesn't cover the grid
            setGridPreview(null);
          }
          setFloatingGhost(null);
          setHoveredChargeSection(null);
        } else {
          setGridPreview(null);
          setFloatingGhost({
            x: e.clientX,
            y: e.clientY,
            width: 160,
            height: 40,
            clientName: cd.clientName,
            hours: cd.hours,
            color: "#6366F1",
            bgColor: "#E0E6FF",
          });
          setHoveredChargeSection(detectChargeSection(e.clientX, e.clientY));

          // Detect merge target: same ordre_id, different charge
          if (cd.ordreId) {
            const target = detectChargeCard(e.clientX, e.clientY);
            if (target && target.id !== cd.chargeId && target.ordreId === cd.ordreId) {
              setMergeTargetChargeId(target.id);
            } else {
              setMergeTargetChargeId(null);
            }
          } else {
            setMergeTargetChargeId(null);
          }
        }
        return;
      }
    },
    [
      detectWorkerCol,
      isOverGrid,
      isOverSidebar,
      getFilteredJobs,
      detectChargeSection,
      detectChargeCard,
      gridScrollRef,
    ],
  );

  /**
   * Run the pause-overlap check then place the magnet (job move or charge drop).
   * Shared by the normal drop path and the métier-mismatch confirmation, so the
   * lunch modal still chains correctly after forcing a métier mismatch.
   */
  const finalizePlacement = useCallback((p: {
    type: "job" | "charge";
    jobId?: string;
    chargeId?: string;
    workerId: string;
    startHour: number;
    durationHours: number;
    date: string;
  }) => {
    const endHour = p.startHour + p.durationHours;
    const pauseZone = findOverlappingPauseZone(p.workerId, p.date, p.startHour, endHour);
    if (pauseZone) {
      const off = workerOffIntervals(useDispoAtelierStore.getState().blocks, p.workerId, p.date, HOURS_START, HOURS_END);
      const head = off[0], tail = off[off.length - 1];
      setLunchOverlapPending({
        dayStart: head && head[0] <= HOURS_START && head[1] < HOURS_END ? head[1] : undefined,
        dayEnd: tail && tail[1] >= HOURS_END && tail[0] > HOURS_START ? tail[0] : undefined,
        type: p.type,
        jobId: p.jobId,
        chargeId: p.chargeId,
        workerId: p.workerId,
        startHour: p.startHour,
        durationHours: p.durationHours,
        date: p.date,
        lunchStart: pauseZone.start,
        lunchEnd: pauseZone.end,
      });
    } else if (p.type === "job" && p.jobId) {
      updateJob(p.jobId, { startHour: p.startHour, durationHours: p.durationHours, workerId: p.workerId, date: p.date });
    } else if (p.type === "charge" && p.chargeId) {
      placeChargeAsJob(p.chargeId, p.workerId, p.startHour, p.durationHours, p.date);
    }
  }, [updateJob, placeChargeAsJob]);

  /** User confirmed forcing a magnet onto a different-métier compagnon → place it.
   *  `snooze` : l'utilisateur a demandé qu'on ne lui repose plus la question
   *  pendant 24 h ou 7 jours. */
  const confirmMetierMismatch = useCallback((snooze: SnoozeDuration | null = null) => {
    const p = metierMismatchPending;
    if (!p) return;
    if (snooze) {
      snoozeMetierMismatch(snooze);
      // Une seule fois, au moment du choix : ensuite plus rien, c'est tout l'intérêt.
      showNotification({
        type: "default",
        message: `Placements hors métier forcés sans confirmation pendant ${SNOOZE_OPTIONS[snooze].label}`,
      });
    }
    finalizePlacement({
      type: p.type,
      jobId: p.jobId,
      chargeId: p.chargeId,
      workerId: p.workerId,
      startHour: p.startHour,
      durationHours: p.durationHours,
      date: p.date,
    });
    setMetierMismatchPending(null);
  }, [metierMismatchPending, finalizePlacement]);

  const handleGlobalPointerUp = useCallback(() => {
    const gp = gridPreviewRef.current;
    const jd = jobDragRef.current;
    if (jd) {
      if (overDropZoneRef.current && jd.mode === "move") {
        requestJobRemoval(jd.jobId);
      } else if (gp && gp.type === "job") {
        const dropDate = gp.date || useAtelierStore.getState().selectedDate;
        const dropEnd = gp.startHour + gp.durationHours;

        // Lâché sur (ou contre) un travail du même ordre : les deux magnets se fondent
        // en un, sans confirmation — empiler exactement les blocs n'est plus nécessaire.
        // Un redimensionnement ne fusionne qu'en chevauchant (le bord à bord est voulu).
        const draggedJob = useAtelierStore.getState().jobs.find((j) => j.id === jd.jobId);
        const mergeTarget = draggedJob?.ordre_id
          ? findMergeableSameOrdreJob(gp.workerId, dropDate, gp.startHour, gp.durationHours, draggedJob.ordre_id, jd.jobId)
          : null;
        const overlapsTarget = !!mergeTarget
          && gp.startHour < mergeTarget.startHour + mergeTarget.durationHours
          && dropEnd > mergeTarget.startHour;
        if (draggedJob?.ordre_id && mergeTarget && (jd.mode === "move" || overlapsTarget)) {
          const merged = mergedRange(mergeTarget, gp, workerDayEnd(gp.workerId, dropDate, Math.min(gp.startHour, mergeTarget.startHour)));
          if (merged.clippedHours > 0) {
            showNotification({ type: "error", message: "Fusion impossible : le bloc fusionné dépasserait la fin de journée" });
          } else if (hasNonMergeableOverlap(gp.workerId, dropDate, merged.startHour, merged.durationHours, jd.jobId, draggedJob.ordre_id)) {
            showNotification({ type: "error", message: "Impossible : le bloc fusionné chevaucherait un autre travail" });
          } else if (findOverlappingPauseZone(gp.workerId, dropDate, merged.startHour, merged.startHour + merged.durationHours)) {
            // Le bloc fusionné passerait sur la pause : on demande avant.
            setJobMergePending({
              draggedJobId: jd.jobId,
              targetJobId: mergeTarget.id,
              mergedStartHour: merged.startHour,
              mergedDurationHours: merged.durationHours,
              draggedClientName: draggedJob.clientName,
              targetClientName: mergeTarget.clientName,
            });
          } else {
            mergeJobs(mergeTarget.id, jd.jobId, merged.startHour, merged.durationHours);
            showNotification({ type: "success", message: `Fusionné avec ${mergeTarget.clientName}` });
          }
          jobDragRef.current = null;
          setGridPreview(null);
          setFloatingGhost(null);
          setOverDropZone(false);
          return;
        }

        if (hasOverlap(gp.workerId, dropDate, gp.startHour, gp.durationHours, jd.jobId)) {
          showNotification({ type: "error", message: "Impossible : ce créneau chevauche un autre travail" });
        } else if (jd.mode === "move") {
          // Métier mismatch? Prompt to confirm forcing a magnet onto a different-métier dispo
          const movedJob = useAtelierStore.getState().jobs.find((j) => j.id === jd.jobId);
          const mismatch = movedJob
            ? getMismatchZone(gp.workerId, dropDate, gp.startHour, dropEnd, movedJob.specialty)
            : null;
          if (mismatch && movedJob && !isMetierMismatchSnoozed()) {
            setMetierMismatchPending({
              type: "job",
              jobId: jd.jobId,
              workerId: gp.workerId,
              startHour: gp.startHour,
              durationHours: gp.durationHours,
              date: dropDate,
              zoneMetier: mismatch,
              magnetMetier: movedJob.specialty,
            });
          } else {
            finalizePlacement({
              type: "job",
              jobId: jd.jobId,
              workerId: gp.workerId,
              startHour: gp.startHour,
              durationHours: gp.durationHours,
              date: dropDate,
            });
          }
        } else {
          // Resize: place directly, no métier/lunch prompt (slot métier unchanged)
          const updates: Parameters<typeof updateJob>[1] = {
            startHour: gp.startHour,
            durationHours: gp.durationHours,
            workerId: gp.workerId,
          };
          if (gp.date) updates.date = gp.date;
          updateJob(jd.jobId, updates);
        }
      }
      jobDragRef.current = null;
      setGridPreview(null);
      setFloatingGhost(null);
      setOverDropZone(false);
      return;
    }

    const cd = chargeDragRef.current;
    if (cd) {
      // Charge-on-charge merge (same ordre)
      if (mergeTargetChargeIdRef.current) {
        mergeCharges(mergeTargetChargeIdRef.current, cd.chargeId);
        chargeDragRef.current = null;
        setGridPreview(null);
        setFloatingGhost(null);
        setHoveredChargeSection(null);
        setMergeTargetChargeId(null);
        return;
      }

      const chargeMergeTarget = gp && gp.type === "charge"
        ? findChargeMergeTarget(cd.chargeId, cd.ordreId, gp.workerId, gp.date || useAtelierStore.getState().selectedDate, gp.startHour, gp.durationHours)
        : null;
      if (gp && gp.type === "charge" && chargeMergeTarget && cd.ordreId) {
        // Charge lâchée sur (ou contre) un travail du même ordre : le travail grandit,
        // la charge perd ce qui est posé — ce qui ne tient pas dans la journée y reste.
        const dropDate = gp.date || useAtelierStore.getState().selectedDate;
        const t = chargeMergeTarget;
        const merged = mergedRange(t, gp, workerDayEnd(gp.workerId, dropDate, Math.min(gp.startHour, t.startHour)));
        const added = +(merged.durationHours - t.durationHours).toFixed(2);
        if (added <= 0) {
          showNotification({ type: "error", message: "Plus de place dans la journée pour fusionner cette charge" });
        } else if (hasNonMergeableOverlap(gp.workerId, dropDate, merged.startHour, merged.durationHours, t.id, cd.ordreId)) {
          showNotification({ type: "error", message: "Impossible : le bloc fusionné chevaucherait un autre travail" });
        } else if (findOverlappingPauseZone(gp.workerId, dropDate, merged.startHour, merged.startHour + merged.durationHours)) {
          setJobMergePending({
            draggedJobId: "",
            draggedChargeId: cd.chargeId,
            targetJobId: t.id,
            mergedStartHour: merged.startHour,
            mergedDurationHours: merged.durationHours,
            draggedClientName: cd.clientName,
            targetClientName: t.clientName,
          });
        } else {
          mergeChargeIntoJob(cd.chargeId, t.id, merged.startHour, merged.durationHours);
          const left = +(cd.hours - added).toFixed(2);
          showNotification({
            type: "success",
            message: `Fusionné avec ${t.clientName}${left > 0 ? ` — ${left} h restent à planifier` : ""}`,
          });
        }
      } else if (gp && gp.type === "charge") {
        const dropDate = gp.date || useAtelierStore.getState().selectedDate;
        if (hasOverlap(gp.workerId, dropDate, gp.startHour, gp.durationHours)) {
          placeChargeAroundBusy(cd.chargeId, gp.workerId, dropDate, gp.startHour);
        } else {
          const jobEnd = gp.startHour + gp.durationHours;
          const leftHours = +(cd.hours - gp.durationHours).toFixed(2);
          if (leftHours > 0) {
            showNotification({
              type: "default",
              message: `${gp.durationHours} h posées jusqu'à la fin de journée — ${leftHours} h restent à planifier`,
            });
          }
          // Métier mismatch? Prompt to confirm forcing the charge onto a different-métier dispo
          const droppedCharge = useAtelierStore.getState().charges.find((c) => c.id === cd.chargeId);
          const mismatch = droppedCharge
            ? getMismatchZone(gp.workerId, dropDate, gp.startHour, jobEnd, droppedCharge.specialty)
            : null;
          if (mismatch && droppedCharge && !isMetierMismatchSnoozed()) {
            setMetierMismatchPending({
              type: "charge",
              chargeId: cd.chargeId,
              workerId: gp.workerId,
              startHour: gp.startHour,
              durationHours: gp.durationHours,
              date: dropDate,
              zoneMetier: mismatch,
              magnetMetier: droppedCharge.specialty,
            });
          } else {
            finalizePlacement({
              type: "charge",
              chargeId: cd.chargeId,
              workerId: gp.workerId,
              startHour: gp.startHour,
              durationHours: gp.durationHours,
              date: dropDate,
            });
          }
        }
      } else if (hoveredChargeSectionRef.current) {
        const section = hoveredChargeSectionRef.current;
        if (section === "en_pause") {
          setReportPausePending({
            chargeId: cd.chargeId,
            targetStatus: section,
            clientName: cd.clientName,
          });
        } else {
          moveChargeToStatus(cd.chargeId, section);
        }
      }
      chargeDragRef.current = null;
      setGridPreview(null);
      setFloatingGhost(null);
      setHoveredChargeSection(null);
      setMergeTargetChargeId(null);
      return;
    }
  }, [
    updateJob,
    placeChargeAsJob,
    requestJobRemoval,
    moveChargeToStatus,
    mergeCharges,
    mergeJobs,
    mergeChargeIntoJob,
    finalizePlacement,
  ]);

  const handleChargeDragStart = useCallback(
    (e: React.PointerEvent, charge: WorkCharge) => {
      // Pointer capture is now handled by ChargeCard itself (on the card div)
      chargeDragRef.current = {
        chargeId: charge.id,
        hours: charge.hours,
        clientName: charge.clientName,
        ordreId: charge.ordre_id,
      };
      const { SNAP_PX } = atelierMetrics(useAtelierStore.getState().compaction);
      setFloatingGhost({
        x: e.clientX,
        y: e.clientY,
        width: 160,
        height: Math.max((charge.hours / SNAP_HOUR) * SNAP_PX, 40),
        clientName: charge.clientName,
        hours: charge.hours,
        color: "#6366F1",
        bgColor: "#E0E6FF",
      });

      // Auto-navigate to the charge's car window date if not currently visible.
      // Jamais en vue jour : l'utilisateur a choisi son jour et veut y poser la
      // charge — sauter à la réception (le 13 au lieu du 14, Héric 2026-10-13)
      // rendait le jour affiché inatteignable.
      const depositDate = charge.carWindow?.depositDate;
      if (!depositDate) return;
      const restDate = charge.carWindow?.restitutionDate || depositDate;
      const { selectedDate, viewMode, setSelectedDate } = useAtelierStore.getState();
      if (viewMode !== "jour") {
        // Week view: only teleport if current week has NO overlap with the window
        const monday = getMonday(selectedDate);
        const friday = new Date(monday);
        friday.setDate(monday.getDate() + 4);
        const weekOverlapsWindow = depositDate <= toISO(friday) && restDate >= toISO(monday);
        if (!weekOverlapsWindow) {
          setSelectedDate(depositDate);
        }
      }
    },
    [],
  );

  // Arrow keys navigate weeks/days — always active, and shifts preview date when dragging
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      if (!planningArrowsAllowed(e)) return;
      const { selectedDate, viewMode, setSelectedDate } = useAtelierStore.getState();
      e.preventDefault();
      const delta = e.key === "ArrowLeft" ? -(viewMode === "semaine" ? 7 : 1) : (viewMode === "semaine" ? 7 : 1);
      const d = new Date(selectedDate);
      d.setDate(d.getDate() + delta);
      setSelectedDate(toISO(d));

      // If dragging, shift the preview date so the ghost stays in place
      const gp = gridPreviewRef.current;
      if (gp?.date) {
        const pd = new Date(gp.date);
        pd.setDate(pd.getDate() + delta);
        setGridPreview({ ...gp, date: toISO(pd) });
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [setGridPreview]);

  const isDraggingAnything =
    !!(chargeDragRef.current || jobDragRef.current) ||
    !!(floatingGhost || gridPreview);
  const isDraggingJob =
    !!jobDragRef.current && jobDragRef.current.mode === "move";

  return {
    jobDragRef,
    gridPreview,
    setGridPreview,
    floatingGhost,
    overDropZone,
    hoveredChargeSection,
    mergeTargetChargeId,
    isDraggingAnything,
    isDraggingJob,
    handleGlobalPointerMove,
    handleGlobalPointerUp,
    handleChargeDragStart,
    lunchOverlapPending,
    setLunchOverlapPending,
    reportPausePending,
    setReportPausePending,
    jobMergePending,
    setJobMergePending,
    metierMismatchPending,
    setMetierMismatchPending,
    confirmMetierMismatch,
  };
}
